(function () {
  // Shared "a desktop menu is open" flag on the header wrapper. Both the mega
  // menu and the mega submenu set their own specific class (header-mega-menu-opened /
  // header-mega-opened); this derives the single class the island CSS keys off, so
  // closing one menu never drops the island while the other is still open.
  const syncHeaderMenuOpen = (headerWrapper) => {
    if (!headerWrapper) return;
    const isOpen =
      headerWrapper.classList.contains("header-mega-menu-opened") ||
      headerWrapper.classList.contains("header-mega-opened");
    headerWrapper.classList.toggle("header-menu-opened", isOpen);
  };

  // mega submenu cleanup runs on every re-init (initHeader fires on
  // shopify:section:load AND unload) so listeners/observers never leak.
  let megaSubmenuCleanup = null;

  const initDesktopMegaSubmenu = (header) => {
    if (typeof megaSubmenuCleanup === "function") {
      megaSubmenuCleanup();
      megaSubmenuCleanup = null;
    }

    const megaSubmenuLinks = header.querySelectorAll(
      ".header-inline-menu__item--megasubmenu"
    );
    if (!megaSubmenuLinks || !megaSubmenuLinks.length) return;

    const headerEl = header.querySelector(".header-inner");
    if (!headerEl) return;

    const headerWrapper = header.querySelector(".header-wrapper");

    const abortController = new AbortController();
    const signal = abortController.signal;
    let observer = null;
    let closeTimeout = null;

    megaSubmenuCleanup = () => {
      abortController.abort();
      clearTimeout(closeTimeout);
      if (observer) observer.disconnect();
    };

    megaSubmenuLinks.forEach((link) => {
      const megaSubmenuEl = link.querySelector(".mega-submenu");
      if (!megaSubmenuEl) return;

      megaSubmenuEl.classList.add("mega-submenu--initialized");
    });

    const calcMegaSubmenuPosition = () => {
      const headerRect = headerEl.getBoundingClientRect();

      megaSubmenuLinks.forEach((link) => {
        const megaSubmenuEl = link.querySelector(".mega-submenu");
        if (!megaSubmenuEl) return;

        const linkMenuRect = link.getBoundingClientRect();
        const megaSubmenuRect = megaSubmenuEl.getBoundingClientRect();

        const extraHeight = Math.abs(linkMenuRect.bottom - megaSubmenuRect.top);
        const extraHeightFix = Math.ceil(extraHeight);
        const extraLeft = Math.ceil(linkMenuRect.left - headerRect.left - 8);
        const extraWidth = Math.ceil(linkMenuRect.width + 16);

        megaSubmenuEl.style.setProperty(
          "--extra-height",
          `${extraHeightFix}px`
        );
        megaSubmenuEl.style.setProperty("--extra-left", `${extraLeft}px`);
        megaSubmenuEl.style.setProperty("--extra-width", `${extraWidth}px`);
      });
    };

    // Open-state class for the header island. The mega panel opens/closes purely
    // on CSS :hover/:focus-within (unchanged) — this only mirrors that state onto
    // the wrapper so the header bar can join the panel as one card. Same grace
    // period + hover re-check as the mega menu, so crossing the hover bridge
    // between the trigger and the panel never drops the island.
    const openMega = () => {
      clearTimeout(closeTimeout);
      headerWrapper?.classList.add("header-mega-opened");
      syncHeaderMenuOpen(headerWrapper);
    };

    const scheduleCloseMega = () => {
      clearTimeout(closeTimeout);
      closeTimeout = setTimeout(() => {
        const stillOpen = Array.from(megaSubmenuLinks).some((item) =>
          item.matches(":hover, :focus-within")
        );
        if (stillOpen) return;

        headerWrapper?.classList.remove("header-mega-opened");
        syncHeaderMenuOpen(headerWrapper);
      }, 100);
    };

    megaSubmenuLinks.forEach((link) => {
      link.addEventListener("mouseenter", openMega, { signal });
      link.addEventListener("focusin", openMega, { signal });

      link.addEventListener(
        "mouseleave",
        () => {
          const focusElement = document.activeElement;
          if (link.contains(focusElement)) focusElement.blur();
          scheduleCloseMega();
        },
        { signal }
      );

      link.addEventListener(
        "focusout",
        (event) => {
          if (!link.contains(event.relatedTarget)) scheduleCloseMega();
        },
        { signal }
      );
    });

    observer = new ResizeObserver(() => calcMegaSubmenuPosition());
    observer.observe(headerEl);
  };

  const initMobileMegaSubmenu = (header) => {
    const megaSubmenuLinks = header.querySelectorAll(
      "header-mobile-drawer .header-mobile-menu__item--megasubmenu"
    );
    if (!megaSubmenuLinks || !megaSubmenuLinks.length) return;

    const openMobileMegaSubmenu = (megaSubmenuEl) => {
      if (
        !megaSubmenuEl ||
        !megaSubmenuEl.classList.contains("mega-submenu-mobile")
      ) {
        return;
      }

      megaSubmenuEl.classList.add("mega-submenu-mobile--visible");
      trapFocus(megaSubmenuEl.querySelector(".mega-submenu-mobile__wrapper"));
    };

    const closeMobileMegaSubmenu = (megaSubmenuEl, parentLink) => {
      if (
        !megaSubmenuEl ||
        !megaSubmenuEl.classList.contains("mega-submenu-mobile")
      ) {
        return;
      }

      megaSubmenuEl.classList.remove("mega-submenu-mobile--visible");
      removeTrapFocus(parentLink);
    };

    megaSubmenuLinks.forEach((item) => {
      // a linked Mega menu tab keeps its title navigable and opens on a separate toggle
      const triggerEl =
        item.querySelector(".js-mega-submenu-mobile-open") ||
        item.querySelector(".header-mobile-menu__link");
      const megaSubmenuEl = item.querySelector(".mega-submenu-mobile");
      if (!triggerEl || !megaSubmenuEl) return;

      const closeButton = megaSubmenuEl.querySelector(
        ".js-mega-submenu-mobile-close-trigger"
      );

      triggerEl.addEventListener("click", (event) => {
        event.preventDefault();
        openMobileMegaSubmenu(megaSubmenuEl);
      });

      closeButton?.addEventListener("click", (event) => {
        event.preventDefault();
        closeMobileMegaSubmenu(megaSubmenuEl, triggerEl);
      });

      megaSubmenuEl.classList.add("mega-submenu-mobile--initialized");
    });
  };

  // Horizontal viewport clamp for the dropdown panels (second-level submenu and
  // its third-level flyout). Both are laid out with a static `left`, so a menu
  // item near a viewport edge pushes its panel off-screen. Panels are measured at
  // their natural position (the shift is cleared first) and only the ones that
  // actually overflow get a --submenu-shift — the rest keep the CSS position.
  const SUBMENU_VIEWPORT_GUTTER = 10;

  const clampSubmenusToViewport = (scope) => {
    const panels = scope.querySelectorAll(
      ".header-inline-submenu, .header-inline-submenu__flyout"
    );
    if (!panels.length) return;

    panels.forEach((panel) => panel.style.removeProperty("--submenu-shift"));

    // clientWidth, so the scrollbar gutter isn't counted as available space
    const maxRight =
      document.documentElement.clientWidth - SUBMENU_VIEWPORT_GUTTER;

    // document order, so a flyout is measured after its parent panel has moved
    // and only takes the shift left over
    panels.forEach((panel) => {
      const rect = panel.getBoundingClientRect();
      // a panel in a display:none branch (mobile) measures 0 — leave it alone
      if (!rect.width) return;

      let shift = 0;
      if (rect.right > maxRight) shift = maxRight - rect.right;
      if (rect.left + shift < SUBMENU_VIEWPORT_GUTTER) {
        shift = SUBMENU_VIEWPORT_GUTTER - rect.left;
      }
      if (!shift) return;

      panel.style.setProperty("--submenu-shift", `${Math.round(shift)}px`);
    });
  };

  // inline submenu cleanup runs on every re-init (initHeader fires on
  // shopify:section:load AND unload) so listeners/observers never leak.
  let submenuCleanup = null;

  const initDesktopSubmenu = (header) => {
    if (typeof submenuCleanup === "function") {
      submenuCleanup();
      submenuCleanup = null;
    }

    const headerBox = header.querySelector(".header-inner");
    if (!headerBox) return;

    const submenuItems = headerBox.querySelectorAll(
      ".header-inline-menu__item--submenu"
    );
    if (!submenuItems || !submenuItems.length) return;

    const headerWrapper = header.querySelector(".header-wrapper");

    const abortController = new AbortController();
    const signal = abortController.signal;
    let closeTimeout = null;

    const calcSubmenuPosition = () => {
      const headerRect = headerBox.getBoundingClientRect();

      submenuItems.forEach((itemEl) => {
        const submenuEl = itemEl.querySelector(".header-inline-submenu");
        if (!submenuEl) return;

        const itemRect = itemEl.getBoundingClientRect();

        const delta = headerRect.bottom - itemRect.bottom + 10;
        const distanceToMenu = Math.ceil(delta / 10) * 10;

        submenuEl.style.top = `calc(100% + ${delta}px)`;
        itemEl.style.setProperty("--distance-to-menu", `${distanceToMenu}px`);

        // second-level list scrolls when longer than the viewport
        const submenuMaxHeight = Math.floor(
          window.innerHeight - headerRect.bottom - 10 - 20
        );
        if (submenuMaxHeight > 0) {
          submenuEl.style.setProperty(
            "--submenu-max-height",
            `${submenuMaxHeight}px`
          );
        }
      });
    };

    const observer = new ResizeObserver(() => {
      calcSubmenuPosition();
      clampSubmenusToViewport(headerBox);
    });

    calcSubmenuPosition();
    clampSubmenusToViewport(headerBox);
    observer.observe(headerBox);

    submenuCleanup = () => {
      abortController.abort();
      clearTimeout(closeTimeout);
      observer.disconnect();
      headerWrapper?.classList.remove("header-submenu-opened");
    };

    // Open-state class for the header. The submenu panel opens/closes purely on
    // CSS :hover/:focus-within (unchanged) — this only mirrors that state onto
    // the wrapper. Same grace period + hover re-check as the mega submenu, so
    // moving between two submenu items never drops the class in between.
    const openSubmenu = () => {
      clearTimeout(closeTimeout);
      headerWrapper?.classList.add("header-submenu-opened");
    };

    const scheduleCloseSubmenu = () => {
      clearTimeout(closeTimeout);
      closeTimeout = setTimeout(() => {
        const stillOpen = Array.from(submenuItems).some((item) =>
          item.matches(":hover, :focus-within")
        );
        if (stillOpen) return;

        headerWrapper?.classList.remove("header-submenu-opened");
      }, 100);
    };

    // the flyout is positioned against the panel (the inner list scrolls),
    // so its top must be synced to the hovered item on hover and on scroll
    const initSubmenuFlyouts = (itemEl) => {
      const submenuEl = itemEl.querySelector(".header-inline-submenu");
      if (!submenuEl) return;

      const scrollEl = submenuEl.querySelector(
        ".header-inline-submenu__scroll"
      );
      const flyoutItems = submenuEl.querySelectorAll(
        ".header-inline-submenu__item--flyout"
      );
      if (!flyoutItems.length) return;

      const placeFlyout = (flyoutItem) => {
        const submenuRect = submenuEl.getBoundingClientRect();
        const flyoutItemRect = flyoutItem.getBoundingClientRect();

        const top = Math.round(flyoutItemRect.top - submenuRect.top);
        flyoutItem.style.setProperty("--flyout-top", `${top}px`);

        const flyoutMaxHeight = Math.floor(
          window.innerHeight - flyoutItemRect.top - 20
        );
        if (flyoutMaxHeight > 0) {
          flyoutItem.style.setProperty(
            "--flyout-max-height",
            `${flyoutMaxHeight}px`
          );
        }
      };

      flyoutItems.forEach((flyoutItem) => {
        flyoutItem.addEventListener(
          "mouseenter",
          () => placeFlyout(flyoutItem),
          { signal }
        );
        flyoutItem.addEventListener("focusin", () => placeFlyout(flyoutItem), {
          signal,
        });
      });

      scrollEl?.addEventListener(
        "scroll",
        () => {
          flyoutItems.forEach((flyoutItem) => {
            if (
              flyoutItem.matches(":hover") ||
              flyoutItem.contains(document.activeElement)
            ) {
              placeFlyout(flyoutItem);
            }
          });
        },
        { signal }
      );
    };

    submenuItems.forEach((itemEl) => {
      initSubmenuFlyouts(itemEl);

      itemEl.addEventListener("mouseenter", openSubmenu, { signal });
      itemEl.addEventListener("focusin", openSubmenu, { signal });

      itemEl.addEventListener(
        "mouseleave",
        () => {
          const focusElement = document.activeElement;
          if (itemEl.contains(focusElement)) {
            focusElement.blur();
          }
          scheduleCloseSubmenu();
        },
        { signal }
      );

      itemEl.addEventListener(
        "focusout",
        (event) => {
          if (!itemEl.contains(event.relatedTarget)) scheduleCloseSubmenu();
        },
        { signal }
      );
    });
  };

  // Page scroll lock for the open desktop mega menu, on the same body-class
  // pattern the drawers/modals use. The class is idempotent on purpose: the panel
  // re-fires its open handler on every mouseenter/focusin, and re-measuring the
  // gutter while it is already reserved would read 0 and drop the compensation.
  const MEGA_MENU_LOCK_CLASS = "overflow-hidden-mega-menu";

  const setPageScrollLock = (isLocked) => {
    const isLockedNow = document.body.classList.contains(MEGA_MENU_LOCK_CLASS);
    if (isLocked === isLockedNow) return;

    if (isLocked) {
      const gutter = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty(
        "--scrollbar-gutter",
        `${gutter}px`
      );
      document.body.classList.add(MEGA_MENU_LOCK_CLASS);
      return;
    }

    document.body.classList.remove(MEGA_MENU_LOCK_CLASS);
    document.documentElement.style.removeProperty("--scrollbar-gutter");
  };

  // Desktop mega menu (Mega menu block): hover-open panel with tabs,
  // per-tab card swipers. Cleanup runs on every re-init (initHeader fires on
  // shopify:section:load AND unload) so swipers/listeners never leak.
  let megaMenuCleanup = null;

  const initDesktopMegaMenu = (header) => {
    if (typeof megaMenuCleanup === "function") {
      megaMenuCleanup();
      megaMenuCleanup = null;
    }

    const megaMenuEl = header.querySelector(".header-mega-menu-trigger");
    if (!megaMenuEl) return;

    const headerWrapper = header.querySelector(".header-wrapper");
    const headerInner = header.querySelector(".header-inner");
    const menuEl = megaMenuEl.querySelector(".header-mega-menu");
    const toggleEl = megaMenuEl.querySelector(
      ".header-mega-menu-trigger__toggle"
    );
    if (!headerWrapper || !headerInner || !menuEl || !toggleEl) return;

    const abortController = new AbortController();
    const signal = abortController.signal;
    const swipers = [];
    const autoplaySwipers = [];
    let closeTimeout = null;
    let resizeObserver = null;

    megaMenuCleanup = () => {
      abortController.abort();
      clearTimeout(closeTimeout);
      // a re-render while the panel is open would otherwise strand the lock
      setPageScrollLock(false);
      if (resizeObserver) resizeObserver.disconnect();
      swipers.forEach((swiper) => {
        if (swiper && !swiper.destroyed) swiper.destroy(true, true);
      });
      swipers.length = 0;
      autoplaySwipers.length = 0;
    };

    // invisible hover bridge between the toggle and the panel (same mechanics
    // as the mega submenu)
    const calcPosition = () => {
      const innerRect = headerInner.getBoundingClientRect();
      const toggleRect = toggleEl.getBoundingClientRect();

      const extraHeight = Math.ceil(
        Math.abs(innerRect.bottom - toggleRect.bottom)
      );
      const extraLeft = Math.ceil(toggleRect.left - innerRect.left - 8);
      const extraWidth = Math.ceil(toggleRect.width + 16);

      menuEl.style.setProperty("--extra-height", `${extraHeight}px`);
      menuEl.style.setProperty("--extra-left", `${extraLeft}px`);
      menuEl.style.setProperty("--extra-width", `${extraWidth}px`);

      const maxHeight = Math.floor(window.innerHeight - innerRect.bottom - 20);
      if (maxHeight > 0) {
        menuEl.style.setProperty("--mega-max-height", `${maxHeight}px`);
      }
    };

    resizeObserver = new ResizeObserver(() => calcPosition());
    resizeObserver.observe(headerInner);

    // tab hover/click switches the visible pane
    const tabs = menuEl.querySelectorAll(".header-mega-menu__tab");
    const panes = menuEl.querySelectorAll(".header-mega-menu__pane");

    // swipers in a display:none pane measure 0 — re-measure once it shows
    const updatePaneSliders = (scope) => {
      scope.querySelectorAll(".js-mega-tab-slider").forEach((sliderEl) => {
        if (sliderEl.swiper) sliderEl.swiper.update();
      });
    };

    const activateTab = (tab) => {
      const tabId = tab.dataset.tabId;
      if (!tabId) return;

      tabs.forEach((item) => item.classList.remove("active"));
      tab.classList.add("active");

      panes.forEach((pane) => {
        pane.classList.toggle("active", pane.dataset.tabId === tabId);
        if (pane.dataset.tabId === tabId) updatePaneSliders(pane);
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener("mouseenter", () => activateTab(tab), { signal });
      tab.addEventListener("focusin", () => activateTab(tab), { signal });
    });

    // Keyboard flow. DOM order is "every tab item, then whichever pane happens to
    // be active", so Tab walked past the tabs and dropped into the pane of the LAST
    // tab it touched — the pane of the tab it was on stayed unreachable. Redirect
    // the boundary presses only, so the walk reads tab -> that tab's pane -> next
    // tab -> … -> page. No tabindex is added: every stop below is already focusable.
    const tabsEl = menuEl.querySelector(".header-mega-menu__tabs");

    const paneFor = (tabId) =>
      tabId
        ? menuEl.querySelector(
            `.header-mega-menu__pane[data-tab-id="${tabId}"]`
          )
        : null;

    const tabFor = (tabId) =>
      Array.from(tabs).find((tab) => tab.dataset.tabId === tabId) || null;

    // The panel lives inside the trigger, so the browser's own "next stop" after
    // the tabs column is the open pane — this is where focus should leave instead.
    const stopAfterMenu = () => {
      const stops = getFocusableElements(document.body);
      const lastInside = stops.reduce(
        (found, stop, index) => (megaMenuEl.contains(stop) ? index : found),
        -1
      );
      return lastInside === -1 ? null : stops[lastInside + 1] || null;
    };

    const moveFocus = (event, element) => {
      if (!element) return;
      event.preventDefault();
      element.focus();
    };

    const onMenuKeydown = (event) => {
      if (event.key !== "Tab") return;

      const target = event.target;
      const columnStops = getFocusableElements(tabsEl);
      const columnIndex = columnStops.indexOf(target);
      const activePane = menuEl.querySelector(".header-mega-menu__pane.active");

      if (columnIndex !== -1) {
        if (event.shiftKey) {
          // step back into the previous tab's pane, so both directions visit the
          // same stops; that pane is display:none until activateTab shows it
          const previous = columnStops[columnIndex - 1];
          const previousPane = previous && paneFor(previous.dataset.tabId);
          if (!previousPane) return;

          activateTab(previous);
          const previousStops = getFocusableElements(previousPane);
          moveFocus(event, previousStops[previousStops.length - 1] || previous);
          return;
        }

        // a tab item hands Tab to its own pane; a plain link tab or the color mode
        // button ends the column and hands it to the page
        const ownPane = paneFor(target.dataset.tabId);
        if (ownPane && ownPane === activePane) {
          const paneStops = getFocusableElements(ownPane);
          if (paneStops.length) {
            moveFocus(event, paneStops[0]);
            return;
          }
        }

        if (columnIndex === columnStops.length - 1) {
          moveFocus(event, stopAfterMenu());
        }
        return;
      }

      if (!activePane || !activePane.contains(target)) return;

      const paneStops = getFocusableElements(activePane);
      const paneIndex = paneStops.indexOf(target);
      if (paneIndex === -1) return;

      if (event.shiftKey) {
        if (paneIndex === 0) moveFocus(event, tabFor(activePane.dataset.tabId));
        return;
      }

      if (paneIndex !== paneStops.length - 1) return;

      const ownTab = tabFor(activePane.dataset.tabId);
      const ownIndex = ownTab ? columnStops.indexOf(ownTab) : -1;
      const nextStop =
        ownIndex === -1 ? null : columnStops[ownIndex + 1] || null;
      moveFocus(event, nextStop || stopAfterMenu());
    };

    if (tabsEl) menuEl.addEventListener("keydown", onMenuKeydown, { signal });

    // A closed panel is only visibility:hidden, so its swipers stay live: autoplay
    // would keep advancing slides and writing the pagination progress every frame
    // for the life of the page. Run it only while the panel is actually open.
    const syncAutoplay = (isOpen) => {
      autoplaySwipers.forEach((swiper) => {
        if (!swiper || swiper.destroyed) return;
        if (isOpen) swiper.autoplay?.start();
        else swiper.autoplay?.stop();
      });
    };

    // open-state class on the wrapper drives aria + the header island styles
    const setOpen = (isOpen) => {
      if (isOpen) initPaneSliders();
      headerWrapper.classList.toggle("header-mega-menu-opened", isOpen);
      syncHeaderMenuOpen(headerWrapper);
      toggleEl.setAttribute("aria-expanded", isOpen);
      syncAutoplay(isOpen);
      setPageScrollLock(isOpen);

      if (isOpen) updatePaneSliders(menuEl);
    };

    // toggle + bridge + panel act as one continuous hover zone: geometry is
    // re-measured on every open (sticky/overlay/announcement-bar states move
    // the toggle), and the delayed close re-checks hover so crossing the gap
    // never collapses the menu
    const openMenu = () => {
      clearTimeout(closeTimeout);
      calcPosition();
      setOpen(true);
    };

    const scheduleClose = () => {
      clearTimeout(closeTimeout);
      closeTimeout = setTimeout(() => {
        if (megaMenuEl.matches(":hover") || menuEl.matches(":hover")) return;

        // A keyboard user inside the panel holds it open even once the pointer has
        // wandered off; focus parked there by a click does not (:focus-visible is
        // the only thing that separates the two), so the mouse still closes it.
        const focusElement = document.activeElement;
        const keptByFocus =
          megaMenuEl.contains(focusElement) &&
          focusElement.matches(":focus-visible");
        if (keptByFocus) return;

        setOpen(false);

        if (megaMenuEl.contains(focusElement)) focusElement.blur();
      }, 100);
    };

    megaMenuEl.addEventListener("mouseenter", openMenu, { signal });
    megaMenuEl.addEventListener("mouseleave", scheduleClose, { signal });

    // Tabbing in only turned the panel visible through CSS :focus-within — the JS
    // open state never ran, so aria-expanded stayed false, the header island was
    // never lifted above the page and the promo autoplay never started.
    megaMenuEl.addEventListener("focusin", openMenu, { signal });
    megaMenuEl.addEventListener(
      "focusout",
      (event) => {
        if (megaMenuEl.contains(event.relatedTarget)) return;
        scheduleClose();
      },
      { signal }
    );

    // the panel keeps its own hover state: entering it holds the menu open,
    // leaving it closes (after the same grace period)
    menuEl.addEventListener("mouseenter", openMenu, { signal });
    menuEl.addEventListener("mouseleave", scheduleClose, { signal });

    // per-tab card sliders (products 4 / collections 3 / submenu promo 1)
    menuEl.classList.add("header-mega-menu--initialized");

    // Built on the first open, not on page load: the panel starts closed and many
    // visitors never open it, so its sliders would only cost main-thread time.
    let slidersReady = false;
    const initPaneSliders = () => {
      if (slidersReady) return;
      slidersReady = true;

      menuEl.querySelectorAll(".js-mega-tab-slider").forEach((sliderEl) => {
        if (sliderEl.swiper) sliderEl.swiper.destroy(true, true);

        const group = sliderEl.closest(".header-mega-menu__slider-group");
        const paginationEl = group?.querySelector(
          ":scope > .slider-pagination"
        );
        const prevBtn = sliderEl.querySelector(".slider-nav-button--prev");
        const nextBtn = sliderEl.querySelector(".slider-nav-button--next");

        const slidesPerRow = Number(sliderEl.dataset.slidesPerRow || 4);
        const slidesPerRowNarrow = Number(
          sliderEl.dataset.slidesPerRow2 || slidesPerRow
        );
        const hasAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
        const autoplayDelay = Number(sliderEl.dataset.autoplayDelay || 5000);

        // Only an autoplaying slider keeps the played bullets filled (the row then
        // reads as a progress track); otherwise the fill marks the active bullet alone.
        if (paginationEl) {
          paginationEl.classList.toggle(
            "slider-pagination--autoplay",
            hasAutoplay
          );
        }

        // The panel is hover-opened, so the pointer sits on the slider whenever the
        // arrows or bullets are used — and pauseOnMouseEnter parks the timer there.
        // Swiper keeps reporting that parked countdown, rearmed to a FULL delay by
        // the manual change, so the frames read "nothing played yet" and the active
        // bullet stays empty while the ones before it are filled: the trail marks
        // the previous slide. Parked, drop the variable instead and let the bullet
        // fall back to its CSS default (a full bar) — the same current-slide marker
        // a slider without autoplay shows.
        let autoplayParked = false;

        const setBulletProgress = (value) => {
          if (!paginationEl) return;
          if (value === null) {
            paginationEl.style.removeProperty("--slider-progress");
            return;
          }
          paginationEl.style.setProperty("--slider-progress", value);
        };

        if (hasAutoplay) {
          sliderEl.addEventListener(
            "pointerenter",
            (event) => {
              if (event.pointerType !== "mouse") return;
              autoplayParked = true;
              setBulletProgress(null);
            },
            { signal }
          );

          sliderEl.addEventListener(
            "pointerleave",
            (event) => {
              if (event.pointerType !== "mouse") return;
              autoplayParked = false;
            },
            { signal }
          );
        }

        const swiperInstance = new Swiper(sliderEl, {
          slidesPerView: slidesPerRowNarrow,
          breakpoints: {
            1536: {
              slidesPerView: slidesPerRow,
            },
          },
          spaceBetween: 24,
          speed: 800,
          watchSlidesProgress: true,
          allowTouchMove: true,
          // forceToAxis leaves the vertical delta to the pane, which scrolls its own
          // overflow, so only a horizontal trackpad gesture reaches the slider.
          mousewheel: {
            forceToAxis: true,
          },
          a11y: {
            scrollOnFocus: false,
          },
          autoplay: hasAutoplay
            ? {
                delay: autoplayDelay,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false,
          pagination: paginationEl
            ? {
                el: paginationEl,
                clickable: true,
              }
            : false,
          navigation: {
            nextEl: nextBtn,
            prevEl: prevBtn,
            disabledClass: "swiper-button-disabled",
          },
          on: {
            autoplayTimeLeft(swiper, time, progress) {
              if (autoplayParked) return;
              setBulletProgress(1 - progress);
            },
            slideChange() {
              // The fill belongs to the slide that is counting down, so it starts
              // from zero on every change the timer is actually running for.
              if (hasAutoplay && !autoplayParked) setBulletProgress(0);
            },
          },
        });

        swipers.push(swiperInstance);
        if (hasAutoplay) autoplaySwipers.push(swiperInstance);
      });
    };
  };

  const initHeaderOverlay = (header) => {
    const main = document.getElementById("MainContent");
    const sections = main.querySelectorAll(".shopify-section");
    const breadcrumbs = document.querySelector("#breadcrumbs");
    const hasOverlayMode = Boolean(
      header.querySelector(".header-wrapper--overlay-mode")
    );
    let isOverlayEnabled = false;

    const removeColorScheme = (element) => {
      if (!element) return;
      let classNames = element.getAttribute("class");
      classNames = classNames.replace(/color-background-\d+/g, "");
      element.setAttribute("class", classNames);
    };

    if (sections.length > 0 && hasOverlayMode) {
      const hasTargetSection =
        sections[0].classList.contains("has-header-overlay");
      const sectionTargetChild = sections[0].querySelector(
        "[data-header-overlay]"
      );
      const headerGroupSections = document.querySelectorAll(
        ".shopify-section-group-header-group"
      );

      if (hasTargetSection && sectionTargetChild) {
        if (headerGroupSections[headerGroupSections.length - 1] === header) {
          isOverlayEnabled = true;
          sections[0].classList.remove("not-margin");
          header.classList.remove("not-mode");
          if (breadcrumbs) {
            removeColorScheme(breadcrumbs);
            const colorScheme = sectionTargetChild.dataset.colorScheme;
            if (colorScheme) breadcrumbs.classList.add(`color-${colorScheme}`);
          }
        } else {
          isOverlayEnabled = false;
          sections[0].classList.add("not-margin");
          header.classList.add("not-mode");
          if (breadcrumbs) removeColorScheme(breadcrumbs);
        }
      } else {
        isOverlayEnabled = false;
        header.classList.add("not-mode");
        if (breadcrumbs) removeColorScheme(breadcrumbs);
      }
    } else {
      isOverlayEnabled = false;
      header.classList.add("not-mode");
      if (breadcrumbs) removeColorScheme(breadcrumbs);
    }

    const controlHoveredState = () => {
      const headerWrapper = header.querySelector(".header-wrapper");
      if (!headerWrapper) return;

      const triggeredItems = headerWrapper.querySelectorAll(
        '.header-inline-menu__item[data-show-on-hover="true"], .header-mega-menu-trigger[data-show-on-hover="true"]'
      );

      let removeHoverTimeout = null;
      let removeAnimateTimeout = null;

      triggeredItems.forEach((triggeredItem) => {
        triggeredItem.addEventListener("mouseenter", () => {
          clearTimeout(removeHoverTimeout);
          clearTimeout(removeAnimateTimeout);

          headerWrapper.classList.add("header-hover-triggered");
          headerWrapper.classList.add("header-hover-animate");
        });

        triggeredItem.addEventListener("mouseleave", () => {
          removeHoverTimeout = setTimeout(() => {
            const isStillHovered = Array.from(triggeredItems).some((el) =>
              el.matches(":hover")
            );

            if (!isStillHovered) {
              headerWrapper.classList.remove("header-hover-triggered");

              removeAnimateTimeout = setTimeout(() => {
                headerWrapper.classList.remove("header-hover-animate");
              }, 300);
            }
          }, 0);
        });
      });
    };

    if (isOverlayEnabled) {
      controlHoveredState();
    }
  };

  // Fixed an issue where placeholders in sections were not displayed because
  // similar SVGs were in the mega submenu / mega menu with visibility hidden (Shopify
  // placeholder_svg_tag + clip-path bug). Build/trial-time only: production
  // themes ship no placeholders, so this is a no-op there.
  const stripPlaceholderClipPaths = (header) => {
    if (!header.querySelector("svg.placeholder-svg")) return;

    document
      .querySelectorAll("svg.placeholder-svg g[clip-path]")
      .forEach((el) => {
        el.removeAttribute("clip-path");
      });
  };

  const initHeader = () => {
    const header = document.querySelector(".shopify-section-header");
    if (!header) return;

    stripPlaceholderClipPaths(header);
    initHeaderOverlay(header);
    initDesktopMegaMenu(header);
    initDesktopMegaSubmenu(header);
    initMobileMegaSubmenu(header);
    initDesktopSubmenu(header);
  };

  initHeader();

  document.addEventListener("shopify:section:load", initHeader);
  document.addEventListener("shopify:section:unload", initHeader);
  document.addEventListener("shopify:section:reorder", initHeader);
})();

if (!customElements.get("header-search")) {
  customElements.define(
    "header-search",
    class HeaderSearch extends HTMLElement {
      constructor() {
        super();

        this.open = this.onOpen.bind(this);
        this.close = this.onClose.bind(this);
        this.onKeyDownBody = this.onKeydownBody.bind(this);
        this.onKeyDownOption = this.onKeydownOption.bind(this);
        this.onKeyUpOption = this.onKeyupOption.bind(this);
        this.syncQueryState = this.onQueryChange.bind(this);
        this.hideFooter = this.onSearchReset.bind(this);
      }

      connectedCallback() {
        this.header = document.querySelector(".header-wrapper");
        this.openBtn = this.querySelector(".js-search-modal-trigger");
        this.modalEl = document.querySelector(".header-search-modal");
        if (!this.modalEl) return;

        this.closeBtn = this.modalEl.querySelector(".js-search-close-trigger");
        this.input = this.modalEl.querySelector("#Search-In-Modal");
        this.overlayEl = this.modalEl.querySelector(
          ".header-search-modal__overlay"
        );
        this.menuEl = this.modalEl.querySelector(".header-search-modal__body");
        this.footerEl = this.modalEl.querySelector(
          ".header-search-modal__footer"
        );
        this.resetBtn = this.modalEl.querySelector(".js-search-input-reset");
        this.predictiveSearchEl =
          this.modalEl.querySelector("predictive-search");

        this.isMac = this.identifyDevice();

        this.openBtn?.addEventListener("click", this.open);
        this.closeBtn?.addEventListener("click", this.close);
        this.overlayEl?.addEventListener("click", this.close);

        this.formEl = this.input ? this.input.form : null;
        this.input?.addEventListener("input", this.syncQueryState);
        this.formEl?.addEventListener("reset", this.syncQueryState);
        this.resetBtn?.addEventListener("click", this.hideFooter);

        document.body.addEventListener("keydown", this.onKeyDownBody);
        this.modalEl.addEventListener("keydown", this.onKeyDownOption);
        this.modalEl.addEventListener("keyup", this.onKeyUpOption);
      }

      disconnectedCallback() {
        document.body.removeEventListener("keydown", this.onKeyDownBody);
        this.input?.removeEventListener("input", this.syncQueryState);
        this.formEl?.removeEventListener("reset", this.syncQueryState);
        this.resetBtn?.removeEventListener("click", this.hideFooter);
      }

      // toggles the All Results footer: visible while the input holds a query
      onQueryChange() {
        window.requestAnimationFrame(() => {
          const hasQuery = !!this.input && this.input.value.trim().length > 0;
          if (hasQuery) this.releaseFooter();
          this.modalEl.classList.toggle(
            "header-search-modal--has-query",
            hasQuery
          );
        });
      }

      // The reset button cancels the native form reset, so the modal state can
      // still match the CSS rules that reveal the footer — an inline display
      // outranks them until a new query or a reopen releases it.
      onSearchReset() {
        this.modalEl.classList.remove("header-search-modal--has-query");
        if (this.footerEl) this.footerEl.style.display = "none";
      }

      releaseFooter() {
        this.footerEl?.style.removeProperty("display");
      }

      onOpen(event) {
        if (event) event.preventDefault();
        if (this.header) this.header.preventHide = true;
        this.isOpen = true;
        this.modalEl.setAttribute("open", true);
        this.releaseFooter();
        this.syncQueryState();
        if (this.openBtn) this.openBtn.setAttribute("aria-expanded", "true");

        this.modalEl.addEventListener(
          "transitionend",
          () => {
            if (this.isOpen) {
              trapFocus(this.modalEl);
            }
          },
          { once: true }
        );

        // using setTimeout makes the modal window appear more smoothly
        this._focusTimeout = setTimeout(() => {
          if (this.input) this.input.focus();
        }, 400);

        document.body.classList.add("overflow-hidden-search-open");

        // styles for forced disable body scroll on iOS
        if (typeof window.lockScrollBodyOnIos === "function") {
          this.openScrollY = window.scrollY;
          window.lockScrollBodyOnIos(this.openScrollY);
        }
      }

      onClose(event, elementToFocus = null) {
        if (event) event.preventDefault();
        if (!this.isOpen) return;
        clearTimeout(this._focusTimeout);
        this.isOpen = false;
        this.modalEl.removeAttribute("open");
        if (this.openBtn) this.openBtn.setAttribute("aria-expanded", "false");
        if (this.header) this.header.preventHide = false;

        document.body.classList.remove("overflow-hidden-search-open");

        if (this.input) {
          this.input.setAttribute("aria-activedescendant", "");
        }

        const newFocusElement = elementToFocus ? elementToFocus : this.openBtn;
        removeTrapFocus(newFocusElement);

        // styles for forced disable body scroll on iOS
        if (typeof window.unlockScrollBodyOnIos === "function") {
          window.unlockScrollBodyOnIos(this.openScrollY);
        }
      }

      onKeyupOption(event) {
        const isOpen = this.modalEl.getAttribute("open");
        if (!isOpen || !(this.menuEl || this.predictiveSearchEl)) return;
        if (event.code === "Enter") {
          const target = event.target;

          if (target.tagName.toLowerCase() === "button") {
            return;
          }

          event.preventDefault();
          this.selectOption();
        }
      }

      onKeydownOption(event) {
        const isOpen = this.modalEl.getAttribute("open");
        if (!isOpen || !(this.menuEl || this.predictiveSearchEl)) return;

        if (event.code === "ArrowUp" || event.code === "ArrowDown") {
          const targetTag = event.target.tagName.toLowerCase();
          if (
            targetTag !== "input" &&
            targetTag !== "a" &&
            targetTag !== "div"
          ) {
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();
          this.switchOption(event.code === "ArrowUp" ? "up" : "down");
        }
      }

      onKeydownBody(event) {
        const isOpen = this.modalEl.getAttribute("open");

        if (isOpen && event.key === "Escape") {
          this.close();
          return;
        }

        if (!isOpen) {
          const key = event?.key?.toLowerCase();
          const isMac = this.isMac;
          const isCmdK = isMac && event?.metaKey && key === "k";
          const isCtrlK = !isMac && event?.ctrlKey && key === "k";

          if (isCmdK || isCtrlK) {
            event.preventDefault();
            this.open();
          }
        }
      }

      switchOption(direction) {
        if (!this.modalEl.getAttribute("open")) return;

        const moveUp = direction === "up";
        const selectedElement = this.modalEl.querySelector(
          '[aria-selected="true"]'
        );

        const allVisibleElements = Array.from(
          this.modalEl.querySelectorAll("[role='option']")
        ).filter((element) => element.offsetParent !== null);

        if (allVisibleElements.length === 0) return;

        let selectedElementIndex = allVisibleElements.indexOf(selectedElement);
        let activeElementIndex = 0;

        if (selectedElementIndex === -1) {
          activeElementIndex = moveUp ? allVisibleElements.length - 1 : 0;
        } else {
          if (moveUp) {
            activeElementIndex =
              selectedElementIndex === 0
                ? allVisibleElements.length - 1
                : selectedElementIndex - 1;
          } else {
            activeElementIndex =
              selectedElementIndex === allVisibleElements.length - 1
                ? 0
                : selectedElementIndex + 1;
          }
        }

        if (this.predictiveSearchEl && this.predictiveSearchEl.statusElement) {
          this.predictiveSearchEl.statusElement.textContent = "";
        }

        const activeElement = allVisibleElements[activeElementIndex];
        if (!activeElement) return;

        activeElement.setAttribute("aria-selected", true);
        if (selectedElement) {
          selectedElement.setAttribute("aria-selected", false);
        }

        if (this.input) {
          this.input.setAttribute("aria-activedescendant", activeElement.id);
        }

        activeElement.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth",
        });
      }

      selectOption() {
        const selectedOption = this.modalEl.querySelector(
          '[aria-selected="true"] a, a[aria-selected="true"]'
        );
        if (selectedOption) selectedOption.click();
      }

      identifyDevice() {
        const isMac =
          /Mac/.test(navigator.platform) ||
          navigator.userAgentData?.platform?.toLowerCase() === "macos" ||
          navigator.userAgentData?.platform?.toLowerCase().includes("mac");

        return isMac;
      }
    }
  );
}

if (!customElements.get("modal-region-selector")) {
  customElements.define(
    "modal-region-selector",
    class ModalRegionSelector extends HTMLElement {
      constructor() {
        super();

        this.open = this.onOpen.bind(this);
        this.close = this.onClose.bind(this);
        this.save = this.onSave.bind(this);
        this.onKeyDownBody = this.onKeydownBody.bind(this);
        this.onTabClick = this.onTabsClick.bind(this);
        this.onSelectClick = this.onSelectItemClick.bind(this);
      }

      connectedCallback() {
        this.header = document.querySelector(".header-wrapper");
        this.openBtn = this.querySelector(".js-region-modal-trigger");
        // the modal renders at the section level, outside the sticky wrapper
        // (same as header-search-modal), so fixed positioning stays viewport-relative
        this.modalEl = document.querySelector("#header-region-modal");
        if (!this.modalEl) return;

        // Move modal to <body> so position:fixed is not trapped inside a
        // transformed ancestor (.shopify-section-header-hidden uses translateY)
        if (this.modalEl.parentElement !== document.body) {
          document.body.appendChild(this.modalEl);
        }

        this.closeBtn = this.modalEl.querySelector(".js-region-close-trigger");
        this.overlayEl = this.modalEl.querySelector(".region-modal__overlay");
        this.saveBtn = this.modalEl.querySelector(".js-region-save-button");
        this.tabsEl = this.modalEl.querySelector(".region-modal__tabs");
        this.tabs = this.modalEl.querySelectorAll(".region-modal__tab");
        this.panes = this.modalEl.querySelectorAll(".region-modal__pane");

        this.openBtn?.addEventListener("click", this.open);
        this.closeBtn?.addEventListener("click", this.close);
        this.overlayEl?.addEventListener("click", this.close);
        this.saveBtn?.addEventListener("click", this.save);
        this.tabsEl?.addEventListener("click", this.onTabClick);
        // capture phase: intercept item clicks BEFORE the global
        // localization-form auto-submit handler (submit happens on Save only)
        this.modalEl.addEventListener("click", this.onSelectClick, true);
        document.body.addEventListener("keydown", this.onKeyDownBody);
      }

      disconnectedCallback() {
        document.body.removeEventListener("keydown", this.onKeyDownBody);
        if (this.isOpen) {
          document.body.classList.remove("overflow-hidden-region-open");
        }
      }

      // both selects must be collapsed whenever the modal opens/closes
      resetSelects() {
        this.modalEl.querySelectorAll("localization-form").forEach((formEl) => {
          if (typeof formEl.hidePanel === "function") formEl.hidePanel();
        });
      }

      onOpen(event) {
        if (event) event.preventDefault();
        if (this.header) this.header.preventHide = true;
        this.isOpen = true;
        this.resetSelects();
        this.modalEl.setAttribute("open", "true");
        this.openBtn?.setAttribute("aria-expanded", "true");
        document.body.classList.add("overflow-hidden-region-open");

        this.modalEl.addEventListener(
          "transitionend",
          () => {
            if (this.isOpen) trapFocus(this.modalEl);
          },
          { once: true }
        );
      }

      onClose(event) {
        if (event) event.preventDefault();
        if (!this.isOpen) return;
        this.isOpen = false;
        this.resetSelects();
        this.modalEl.removeAttribute("open");
        this.openBtn?.setAttribute("aria-expanded", "false");
        document.body.classList.remove("overflow-hidden-region-open");
        if (this.header) this.header.preventHide = false;
        removeTrapFocus(this.openBtn);
      }

      onKeydownBody(event) {
        if (this.isOpen && event.key === "Escape") this.close();
      }

      onTabsClick(event) {
        const tab = event.target.closest(".region-modal__tab");
        if (!tab) return;

        const tabId = tab.dataset.tabId;
        this.tabs.forEach((item) => {
          item.classList.toggle("active", item === tab);
          item.setAttribute("aria-selected", item === tab ? "true" : "false");
        });
        this.panes.forEach((pane) => {
          pane.classList.toggle("active", pane.dataset.tabId === tabId);
        });
      }

      onSelectItemClick(event) {
        const link = event.target.closest(".localization-select__link");
        if (!link || !this.modalEl.contains(link)) return;

        event.preventDefault();
        event.stopPropagation();

        const formEl = link.closest("localization-form");
        if (!formEl) return;

        const input = formEl.querySelector(
          'input[name="country_code"], input[name="locale_code"]'
        );
        if (input) input.value = link.dataset.value;

        formEl
          .querySelectorAll(".localization-select__link")
          .forEach((item) => {
            item.classList.toggle(
              "localization-select__link--active",
              item === link
            );
            if (item === link) item.setAttribute("aria-current", "true");
            else item.removeAttribute("aria-current");
          });

        const valueEl = formEl.querySelector(".localization-select__value");
        // region items split name + currency into separate spans — join them
        const labelEls = link.querySelectorAll(".body-sm");
        if (valueEl && labelEls.length) {
          const labelText = Array.from(labelEls)
            .map((el) => el.textContent)
            .join(" ");
          valueEl.textContent = labelText.replace(/\s+/g, " ").trim();
        }

        const buttonFlagEl = formEl.querySelector(
          ".localization-select__button .localization-select__flag-item"
        );
        const itemFlagEl = link.querySelector(
          ".localization-select__flag-item"
        );
        if (buttonFlagEl && itemFlagEl) {
          buttonFlagEl.src = itemFlagEl.src;
          if (itemFlagEl.srcset) buttonFlagEl.srcset = itemFlagEl.srcset;
        }

        if (typeof formEl.hidePanel === "function") formEl.hidePanel();
      }

      // one submit applies BOTH pending changes: the locale value is injected
      // into the region form so a single localization POST carries both params
      onSave(event) {
        if (event) event.preventDefault();

        const regionFormEl = this.modalEl.querySelector(
          ".localization-select--region form"
        );
        const languageFormEl = this.modalEl.querySelector(
          ".localization-select--language form"
        );
        const baseForm = regionFormEl || languageFormEl;
        if (!baseForm) return;

        if (regionFormEl && languageFormEl) {
          const localeInput = languageFormEl.querySelector(
            'input[name="locale_code"]'
          );
          let sharedLocaleInput = baseForm.querySelector(
            'input[name="locale_code"]'
          );
          if (!sharedLocaleInput) {
            sharedLocaleInput = document.createElement("input");
            sharedLocaleInput.type = "hidden";
            sharedLocaleInput.name = "locale_code";
            baseForm.appendChild(sharedLocaleInput);
          }
          if (localeInput) sharedLocaleInput.value = localeInput.value;
        }

        baseForm.submit();
      }
    }
  );
}

if (!customElements.get("header-dropdown")) {
  customElements.define(
    "header-dropdown",
    class HeaderDropdown extends HTMLElement {
      constructor() {
        super();

        this._onOpen = this.openDropdown.bind(this);
        this._onClose = this.closeDropdown.bind(this);
        this._onOutsideClick = this.onOutsideClick.bind(this);
      }

      connectedCallback() {
        this.openBtn = this.querySelector(".js-dropdown-trigger");
        this.closeBtn = this.querySelector(".js-dropdown-close-trigger");
        this.modalEl = this.querySelector(".header-dropdown__modal");

        this.openBtn?.addEventListener("click", this._onOpen);
        this.closeBtn?.addEventListener("click", this._onClose);
        document.addEventListener("click", this._onOutsideClick);
      }

      disconnectedCallback() {
        this.openBtn?.removeEventListener("click", this._onOpen);
        this.closeBtn?.removeEventListener("click", this._onClose);
        document.removeEventListener("click", this._onOutsideClick);
      }

      openDropdown() {
        if (!this.getAttribute("open")) {
          this.setAttribute("open", "true");
          this.addEventListener("transitionend", () => {
            trapFocus(this.modalEl);
          });
        } else {
          this.closeDropdown();
        }
      }

      closeDropdown() {
        this.removeAttribute("open");
        removeTrapFocus(this.openBtn);
      }

      onOutsideClick(event) {
        if (
          !this.getAttribute("open") ||
          this.contains(event.target) ||
          this.openBtn.contains(event.target)
        ) {
          return;
        }
        this.closeDropdown();
      }
    }
  );
}
